import React from 'react';
import { cx } from './cx';

/**
 * Wizard progress. `steps`: [{ key, label }]. `current` is the active key.
 * `compact` renders a small inline version for page headers (short connectors, no stretching).
 * `onSelect(key)` is only invoked for steps up to and including the furthest
 * reached step (`maxReachedIndex`), so users cannot skip required steps.
 */
export interface StepDef {
  key: string;
  label: string;
}

interface StepperProps {
  steps: StepDef[];
  current: string;
  maxReachedIndex?: number;
  onSelect?: (key: string) => void;
  compact?: boolean;
}

export function Stepper({ steps, current, maxReachedIndex, onSelect, compact = false }: StepperProps) {
  const currentIndex = steps.findIndex((s) => s.key === current);
  const reached = typeof maxReachedIndex === 'number' ? maxReachedIndex : currentIndex;
  return (
    <ol className={cx('flex items-center', compact ? 'gap-1' : 'my-5')} aria-label="Progress">
      {steps.map((s, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        const clickable = onSelect && i <= reached && !active;
        const body = (
          <>
            <span
              className={cx(
                'grid flex-none place-items-center rounded-full font-bold',
                compact ? 'h-6 w-6 text-[11px]' : 'h-7 w-7 text-xs',
                done || active ? 'bg-brand-600 text-white' : 'bg-chip text-muted',
                active && (compact ? 'ring-[3px] ring-brand-100' : 'ring-4 ring-brand-100')
              )}
              aria-hidden="true"
            >
              {done ? '✓' : i + 1}
            </span>
            <span className={cx('font-semibold', compact ? 'text-[13px]' : 'text-sm', active ? 'text-ink' : 'sr-only text-muted sm:not-sr-only')}>{s.label}</span>
          </>
        );
        return (
          <li key={s.key} className={cx('flex items-center', !compact && 'flex-1 last:flex-none')} aria-current={active ? 'step' : undefined}>
            {clickable ? (
              <button type="button" onClick={() => onSelect(s.key)} className={cx('flex items-center rounded-md', compact ? 'gap-2' : 'gap-2.5')}>{body}</button>
            ) : (
              <span className={cx('flex items-center', compact ? 'gap-2' : 'gap-2.5')}>{body}</span>
            )}
            {i < steps.length - 1 && <span className={cx('h-0.5 bg-line', compact ? 'mx-2 w-5 sm:w-6' : 'mx-3 flex-1')} aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}
